import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { apiClient } from '../../lib/api-client.js';
import { Spinner } from '../../components/Spinner.js';

interface Partner {
  id: string;
  nickname: string;
  avatarUrl: string | null;
}

interface LatestMessage {
  id: string;
  body: string;
  senderId: string;
  createdAt: string;
}

interface Conversation {
  id: string;
  partner: Partner;
  latestMessage: LatestMessage | null;
  unreadCount: number;
  updatedAt: string;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'たった今';
  if (minutes < 60) return `${minutes}分前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}時間前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}日前`;
  return new Date(iso).toLocaleDateString('ja-JP');
}

function Avatar({ url, nickname }: { url: string | null; nickname: string }) {
  if (url) {
    return (
      <img src={url} alt={nickname} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
    );
  }
  return (
    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
      <span className="text-gray-500 text-lg font-semibold">{nickname[0]}</span>
    </div>
  );
}

export function InboxView() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    try {
      const data = await apiClient.get<Conversation[]>('/messages', { requireAuth: true });
      setConversations(data ?? []);
    } catch {
      // エラー無視
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="px-4 py-4 border-b border-gray-100">
        <h1 className="text-lg font-bold text-gray-900">メッセージ</h1>
      </div>

      {conversations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3">
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="text-sm">まだメッセージはありません</p>
          <p className="text-xs mt-1">ユーザープロフィールからDMを送りましょう</p>
        </div>
      ) : (
        <ul>
          {conversations.map((conv) => (
            <li key={conv.id}>
              <button
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                onClick={() => navigate(`/messages/${conv.id}`)}
              >
                <Avatar url={conv.partner.avatarUrl} nickname={conv.partner.nickname} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm text-gray-900 truncate">
                      {conv.partner.nickname}
                    </span>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {conv.latestMessage ? formatRelativeTime(conv.latestMessage.createdAt) : ''}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className="text-sm text-gray-500 truncate">
                      {conv.latestMessage?.body ?? ''}
                    </p>
                    {conv.unreadCount > 0 && (
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full">
                        {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
